/**
 * panZoomMath — the pure math of the gym map's pan / pinch / rotate / wheel
 * zoom gestures (D50).
 *
 * Why it exists: the formulas used to live inline in ZoomableMap's native
 * touch listeners, where nothing could test them without a real touch
 * device. Pulling them out lets a unit test pin every number (including an
 * exact-equality oracle against the former inline code), and lets the
 * Spraywall photo component (SprayWallPhoto) reuse the same pan / pinch /
 * clamp math WITHOUT the rotation part.
 *
 * What does NOT live here: the gesture STATE. The refs (finger ids, last
 * distance / midpoint / angle, activation flags), the native listeners,
 * `applyViewBox`, `getScreenCTM()` lookups and the rotation persistence all
 * stay in the component. Every function below is a pure function of its
 * arguments; the component decides WHEN to call it.
 *
 * Client-only on purpose: no other runtime (Functions, MCP) touches map
 * gestures, so this is not `@sendly/shared` material.
 *
 * The extraction is behaviour-identical: same numbers, same operation order
 * (floating point is order-sensitive), same guards. Change a formula here and
 * the oracle in `__tests__/panZoomMath.test.js` fails on purpose.
 */

// ── Gesture thresholds ──
// Each axis (zoom, rotation) has an independent activation threshold so a
// clear-intent pinch never sneaks in a tiny rotation, and a clear-intent twist
// never sneaks in a tiny zoom. Both are measured from the ABSOLUTE start of the
// gesture (not last frame), so slow drift over a long swipe doesn't trickle
// past the threshold the way per-frame accumulation would.
export const PINCH_ACTIVATION_RATIO = 0.05;            // ±5% spread change before pinch zoom kicks in
// Per-frame sensor-noise dead zone is a separate concern from activation: even
// when pinch is active, sub-1% per-frame scale changes are sensor jitter, not
// zoom intent.
export const PINCH_FRAME_DEAD_ZONE = 0.01;
export const ROTATION_ACTIVATION_DEG = 12;             // total twist from start before rotation kicks in
export const ROTATION_PER_FRAME_DEAD_ZONE_DEG = 1.0;   // per-frame deltas below this don't apply

// ── ViewBox limits ──
export const MIN_VIEW_WIDTH = 60;           // closest zoom: 60 map units across
export const MAX_VIEW_WIDTH_FACTOR = 2;     // furthest zoom: twice the map width
export const PAN_OVERSCROLL_RATIO = 0.2;    // the viewBox centre may leave the map by 20% of its size

// ── Tap vs drag ──
export const TAP_SLOP_PX = 5;               // touch: movement within this is still a tap
export const POINTER_DRAG_SLOP_PX = 4;      // mouse: movement within this is still a click

// ── Wheel zoom ──
export const WHEEL_ZOOM_IN = 1.2;
export const WHEEL_ZOOM_OUT = 0.83;

// Forward direction: INNER (route doc) → display position. Markers are
// rendered OUTSIDE the rotated <g> at their pre-rotation position so the
// text glyphs stay upright; we forward-rotate (x, y) around the map
// center here so they end up where they're visually expected. No outer-
// box offset (viewBox doesn't grow), so the math is just rotate-around-
// center.
export function rotatePoint(x, y, rot, mw, mh) {
  if (rot === 0) return { x, y };
  const rad = rot * Math.PI / 180;
  const cos = Math.cos(rad), sin = Math.sin(rad);
  const cx = mw / 2, cy = mh / 2;
  const dx = x - cx, dy = y - cy;
  return {
    x: dx * cos - dy * sin + cx,
    y: dx * sin + dy * cos + cy,
  };
}

// Inverse of the above: convert a viewBox coordinate back to the INNER
// coordinate the route document stores. Used by tap-to-place so a new
// route saved while the map is rotated lands at the correct (x, y).
export function unrotatePoint(ox, oy, rot, mw, mh) {
  if (rot === 0) return { x: ox, y: oy };
  const rad = rot * Math.PI / 180;
  const cos = Math.cos(rad), sin = Math.sin(rad);
  const cx = mw / 2, cy = mh / 2;
  const dx = ox - cx, dy = oy - cy;
  return {
    x: dx * cos + dy * sin + cx,
    y: -dx * sin + dy * cos + cy,
  };
}

// Signed angle difference folded into (-π, π] with a SINGLE wrap — the
// difference of two atan2 results is always within (-2π, 2π), so one step is
// enough and keeps a twist across the ±180° seam from reading as ~360°.
export function wrapAngleDelta(rad) {
  if (rad > Math.PI) rad -= 2 * Math.PI;
  if (rad < -Math.PI) rad += 2 * Math.PI;
  return rad;
}

// Distance, finger-line angle (radians) and midpoint of a two-finger pair.
// Takes anything with clientX / clientY (a Touch).
export function fingerGeometry(t0, t1) {
  const dx = t0.clientX - t1.clientX;
  const dy = t0.clientY - t1.clientY;
  return {
    dist: Math.hypot(dx, dy),
    angle: Math.atan2(dy, dx),
    midX: (t0.clientX + t1.clientX) / 2,
    midY: (t0.clientY + t1.clientY) / 2,
  };
}

// Until the fingers have spread more than ±PINCH_ACTIVATION_RATIO from the
// gesture-start distance, the gesture is a pure pan / rotate and the per-frame
// zoom factor is ignored. No start distance → never activates.
export function isPinchActivated(dist, startDist) {
  return !!startDist && Math.abs(dist / startDist - 1) > PINCH_ACTIVATION_RATIO;
}

// This frame's zoom factor: 1 while pinch is not active, otherwise the spread
// ratio against the previous frame with the sensor-noise dead zone applied.
// The caller guarantees a truthy `lastDist` (the two-finger branch only runs
// with a recorded baseline).
export function pinchFrameFactor(dist, lastDist, pinchActive) {
  if (!pinchActive) return 1;
  const raw = dist / lastDist;
  return Math.abs(raw - 1) < PINCH_FRAME_DEAD_ZONE ? 1 : raw;
}

// Compare the CURRENT finger-line angle against the gesture-start baseline
// (signed, wrap-around safe). Once the absolute deviation crosses
// ROTATION_ACTIVATION_DEG, rotation kicks in.
export function isRotationActivated(angle, startAngle) {
  return startAngle != null && Math.abs(wrapAngleDelta(angle - startAngle) * 180 / Math.PI) > ROTATION_ACTIVATION_DEG;
}

// Zoom limits on the viewBox width (the height follows the map's aspect).
export function clampViewWidth(w, mapWidth) {
  return Math.max(MIN_VIEW_WIDTH, Math.min(mapWidth * MAX_VIEW_WIDTH_FACTOR, w));
}

// Clamp viewBox so its center stays within map bounds (20% overscroll
// margin). Bounds always use mapWidth/mapHeight — the viewBox shape
// doesn't change with rotation in this design, so neither do the limits.
export function clampViewBox(vb, mapWidth, mapHeight) {
  const mx = vb.w * PAN_OVERSCROLL_RATIO, my = vb.h * PAN_OVERSCROLL_RATIO;
  const cx = Math.max(-mx, Math.min(mapWidth + mx, vb.x + vb.w / 2));
  const cy = Math.max(-my, Math.min(mapHeight + my, vb.y + vb.h / 2));
  return { x: cx - vb.w / 2, y: cy - vb.h / 2, w: vb.w, h: vb.h };
}

// Zoom the viewBox by `factor` around the viewBox point (cx, cy), which stays
// put on screen. Returns the clamped viewBox.
export function zoomViewBoxAt(v, factor, cx, cy, mapWidth, mapHeight) {
  const nw = clampViewWidth(v.w / factor, mapWidth);
  const nh = nw * (mapHeight / mapWidth);
  const nx = cx - (cx - v.x) * (nw / v.w);
  const ny = cy - (cy - v.y) * (nh / v.h);
  return clampViewBox({ x: nx, y: ny, w: nw, h: nh }, mapWidth, mapHeight);
}

export function wheelZoomFactor(deltaY) {
  return deltaY < 0 ? WHEEL_ZOOM_IN : WHEEL_ZOOM_OUT;
}

// xMidYMid meet: uniform scale + centering offsets of a viewBox inside the
// element's client rect.
export function meetTransform(rect, vb) {
  const s = Math.min(rect.width / vb.w, rect.height / vb.h);
  const offX = (rect.width - vb.w * s) / 2;
  const offY = (rect.height - vb.h * s) / 2;
  return { s, offX, offY };
}

// The part of viewBox space the element really shows. With "meet" the viewBox
// is only the part that is guaranteed to fit: when the element's aspect differs
// from the viewBox's (a photo in a tall full-window box), the letterbox bands
// show more of the drawing, and culling against the bare viewBox would leave
// holds in those bands undrawn. Without a measurable rect the viewBox stands.
export function visibleViewBox(rect, vb) {
  if (!rect || !(rect.width > 0) || !(rect.height > 0) || !vb || !(vb.w > 0) || !(vb.h > 0)) return vb;
  const { s, offX, offY } = meetTransform(rect, vb);
  return { x: vb.x - offX / s, y: vb.y - offY / s, w: rect.width / s, h: rect.height / s };
}

// One two-finger frame of pan + pinch zoom: the map point that was under the
// previous finger midpoint ends up under the new midpoint, at the new zoom.
// `factor` 1 means no zoom (pure pan). Returns the UNCLAMPED viewBox — the
// caller clamps it.
export function pinchPanViewBox({ v, rect, prevMid, newMid, factor, mapWidth, mapHeight }) {
  const { s, offX, offY } = meetTransform(rect, v);

  // SVG point under previous midpoint
  const svgMidX = (prevMid.x - rect.left - offX) / s + v.x;
  const svgMidY = (prevMid.y - rect.top - offY) / s + v.y;

  // New viewBox size from pinch zoom (factor=1 → no zoom, pure pan)
  const nw = clampViewWidth(v.w / factor, mapWidth);
  const nh = nw * (mapHeight / mapWidth);

  // New scale + offsets for the updated viewBox size
  const { s: ns, offX: nOffX, offY: nOffY } = meetTransform(rect, { w: nw, h: nh });

  // Position viewBox so svgMid appears at new midpoint on screen
  const newVx = svgMidX - (newMid.x - rect.left - nOffX) / ns;
  const newVy = svgMidY - (newMid.y - rect.top - nOffY) / ns;

  return { x: newVx, y: newVy, w: nw, h: nh };
}

// One two-finger frame of ACTIVE rotation (the caller checks activation).
// Pivot is the FINGER MIDPOINT, not map center. The <g> transform itself
// rotates around mapCenter (single source of truth, simpler marker math), but
// we shift the viewBox to compensate so that the INNER content currently under
// the user's fingers stays under them through the rotation. Per-frame dead
// zone (1°) suppresses sensor jitter while pinch zoom is the dominant axis.
//
// `v` must be the viewBox JUST applied by the pinch step of the same frame
// (pinch zoom may have changed v.w/v.h this frame). Returns null when there is
// nothing to apply, otherwise the new rotation (degrees, [0, 360)) and the
// UNCLAMPED shifted viewBox.
export function rotationStep({ v, rect, mid, angle, lastAngle, currentRot, mapWidth, mapHeight }) {
  if (lastAngle == null) return null;
  const dAng = wrapAngleDelta(angle - lastAngle);
  const dDeg = dAng * 180 / Math.PI;
  if (!(Math.abs(dDeg) > ROTATION_PER_FRAME_DEAD_ZONE_DEG)) return null;
  const { s: sCur, offX: offXCur, offY: offYCur } = meetTransform(rect, v);
  const pOuterX = (mid.x - rect.left - offXCur) / sCur + v.x;
  const pOuterY = (mid.y - rect.top - offYCur) / sCur + v.y;
  const pInner = unrotatePoint(pOuterX, pOuterY, currentRot, mapWidth, mapHeight);
  const newRot = ((currentRot + dDeg) % 360 + 360) % 360;
  const pOuterNew = rotatePoint(pInner.x, pInner.y, newRot, mapWidth, mapHeight);
  const shiftX = pOuterNew.x - pOuterX;
  const shiftY = pOuterNew.y - pOuterY;
  return { rotation: newRot, viewBox: { x: v.x + shiftX, y: v.y + shiftY, w: v.w, h: v.h } };
}

// Single-pointer pan (touch or mouse): the viewBox origin for a pointer now at
// (clientX, clientY), given the pan start record `ps` = { x, y (screen), vx, vy
// (viewBox origin), sx, sy (SVG units per screen pixel) }. The caller adds w / h.
export function panOrigin(ps, clientX, clientY) {
  return { x: ps.vx - (clientX - ps.x) * ps.sx, y: ps.vy - (clientY - ps.y) * ps.sy };
}

// Tap vs drag: true once the pointer moved more than `slop` pixels on either axis.
export function exceedsSlop(dx, dy, slop) {
  return Math.abs(dx) > slop || Math.abs(dy) > slop;
}

// The zoomed-out spray wall marker is sized in SCREEN pixels, never in map
// units: map images come in any resolution, and a fixed map-unit scale drew
// the marker tiny on a large image and huge on a small one.
export const SPRAY_MARKER_UNITS = 40;   // SprayWallMarkerGlyph's own size
export const SPRAY_MARKER_FAR_PX = 32;  // its on-screen size when zoomed out

// The scale that draws the glyph SPRAY_MARKER_FAR_PX wide on screen, given the
// current viewBox width (map units across) and the container width (px). A
// missing or zero width falls back to 1, so an unmeasured container never
// draws a zero-size marker.
export function sprayMarkerFarScale(viewBoxW, containerW) {
  if (!(viewBoxW > 0) || !(containerW > 0)) return 1;
  return (SPRAY_MARKER_FAR_PX * viewBoxW) / (SPRAY_MARKER_UNITS * containerW);
}
